import { get, post } from './request';

export interface PropertyWalletItemDTO {
  id: number;
  publicId: string;
  identification?: string;
  alias?: string;
  address: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    formatted?: string;
  };
  type: {
    id?: number;
    code?: string;
    description?: string;
  };
  activeProcesses?: {
    fireInsurance?: number;
    bondInsurance?: number;
    claim?: number;
    order?: number;
    capitalization?: number;
    lenderInsurance?: number;
  };
  area?: number;
  updatedAt?: string;
  score?: number;
}

export interface PropertyWalletEstateContextDTO {
  id: string;
  name: string;
  rvUuid?: string;
}

export interface PropertyWalletListResponseDTO {
  items: PropertyWalletItemDTO[];
  resultsInfo: {
    page: number;
    limit: number;
    total: number;
  };
  estate?: PropertyWalletEstateContextDTO;
}

export interface PropertyTypeOptionDTO {
  id: number;
  description: string;
  code?: string;
}

export interface ActivatePropertiesWalletResponseDTO {
  activated: boolean;
  alreadyActive: boolean;
  estate: {
    id: string;
    name: string;
    rvUuid?: string;
  };
}

export type CreateWalletPropertyPayload = {
  estateId?: string;
  identification?: string;
  registration?: string;
  propertyType: string;
  area?: number;
  furniture?: string;
  suggestedRentalValue?: number;
  suggestedSaleValue?: number;
  address: {
    zipcode: string;
    street: string;
    neighborhood: string;
    number: string;
    complement?: string;
    city: string;
    state: string;
  };
};

export type ImportWalletPropertyPayload = CreateWalletPropertyPayload;

export interface ImportWalletPropertiesResult {
  total: number;
  success: number;
  failed: Array<{
    rowNumber: number;
    identification?: string;
    message: string;
  }>;
}

export interface CreateWalletPropertyResponseDTO {
  id: number;
  publicId: string;
  estate: {
    id: string;
    name: string;
    rvUuid?: string;
  };
}

export type ListPropertiesFilters = {
  estateId?: string;
  search?: string;
  type?: string;
  page?: number;
  limit?: number;
  orderBy?: string;
  orderByDir?: 'ASC' | 'DESC';
};

const buildQuery = (filters: ListPropertiesFilters): string => {
  const params = new URLSearchParams();
  if (filters.estateId) params.set('estateId', filters.estateId);
  if (filters.search) params.set('search', filters.search);
  if (filters.type) params.set('type', filters.type);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.orderBy) params.set('orderBy', filters.orderBy);
  if (filters.orderByDir) params.set('orderByDir', filters.orderByDir);
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const listProperties = async (
  filters: ListPropertiesFilters = {}
): Promise<PropertyWalletListResponseDTO> => {
  return get(`/properties${buildQuery(filters)}`);
};

export const listPropertyTypes = async (): Promise<PropertyTypeOptionDTO[]> => {
  return get('/properties/types');
};

export const activatePropertiesWallet = async (
  estateId?: string
): Promise<ActivatePropertiesWalletResponseDTO> => {
  return post('/properties/activate', {
    estateId
  });
};

export const createWalletProperty = async (
  payload: CreateWalletPropertyPayload
): Promise<CreateWalletPropertyResponseDTO> => {
  return post('/properties', payload);
};

const getImportErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const maybeMessage = (error as { message?: string | string[] }).message;
    if (Array.isArray(maybeMessage)) return maybeMessage.join(', ');
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
  }

  if (error instanceof Error && error.message) return error.message;

  return 'Não foi possível cadastrar este imóvel.';
};

export const importWalletProperties = async (
  properties: ImportWalletPropertyPayload[]
): Promise<ImportWalletPropertiesResult> => {
  const result: ImportWalletPropertiesResult = {
    total: properties.length,
    success: 0,
    failed: []
  };

  for (let index = 0; index < properties.length; index += 1) {
    const property = properties[index];

    try {
      await createWalletProperty(property);
      result.success += 1;
    } catch (error) {
      result.failed.push({
        rowNumber: index + 2,
        identification: property.identification,
        message: getImportErrorMessage(error)
      });
    }
  }

  return result;
};

export interface PropertyWalletLastEventDTO {
  code?: string;
  inspectionType?: string;
  date?: string;
  orderId?: string;
}

export interface PropertyWalletDetailsDTO {
  id?: number;
  publicId: string;
  identification?: string;
  alias?: string;
  area?: number;
  score?: number;
  purpose?: string;
  activeProcesses?: PropertyWalletItemDTO['activeProcesses'];
  updatedAt?: string;
  address: PropertyWalletItemDTO['address'];
  type: PropertyWalletItemDTO['type'];
  registration?: string;
  realStateEnrollment?: string;
  suggestedRentalValue?: number;
  suggestedSaleValue?: number;
  condoFee?: number;
  parkingSpaces?: number;
  owner?: string;
  furniture?: string;
  iptu?: number;
  lastEvents?: PropertyWalletLastEventDTO[];
}

export type PropertyHistoricEventType =
  | 'ORDER'
  | 'FIRE_INSURANCE'
  | 'BOND_INSURANCE'
  | 'CLAIM'
  | 'CAPITALIZATION'
  | 'LENDER_INSURANCE'
  | 'PROPERTY_CREATED'
  | 'PROPERTY_UPDATED'
  | 'PROPERTY'
  | string;

export interface PropertyWalletHistoryInfosSummaryDTO {
  status?: string;
  claim_type?: string;
  opened_at?: string;
  insured_name?: string;
  tenant_name?: string;
  product?: string;
  protocol_number?: string;
  [key: string]: unknown;
}

export interface PropertyWalletHistoryItemDTO {
  id?: number;
  publicId?: string;
  code?: string;
  orderId?: string;
  identifier?: string;
  inspectionType?: string;
  date?: string;
  eventType?: PropertyHistoricEventType;
  application?: string;
  remoteId?: string;
  occurredAt?: string;
  infosSummary?: PropertyWalletHistoryInfosSummaryDTO;
}

export interface PropertyWalletHistoryResponseDTO {
  items: PropertyWalletHistoryItemDTO[];
  resultsInfo: {
    page: number;
    limit: number;
    total: number;
  };
}

export type ListPropertyHistoryFilters = {
  estateId?: string;
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
};

const buildHistoryQuery = (
  propertyPublicId: string,
  filters: ListPropertyHistoryFilters
): string => {
  const params = new URLSearchParams();
  if (filters.estateId) params.set('estateId', filters.estateId);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.search) params.set('search', filters.search);
  if (filters.type) params.set('type', filters.type);
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getPropertyDetails = async (
  propertyPublicId: string,
  estateId?: string
): Promise<PropertyWalletDetailsDTO> => {
  const params = new URLSearchParams();
  if (estateId) params.set('estateId', estateId);
  const query = params.toString();
  return get(`/properties/${propertyPublicId}${query ? `?${query}` : ''}`);
};

export const getPropertyHistory = async (
  propertyPublicId: string,
  filters: ListPropertyHistoryFilters = {}
): Promise<PropertyWalletHistoryResponseDTO> => {
  return get(
    `/properties/${propertyPublicId}/historic${buildHistoryQuery(propertyPublicId, filters)}`
  );
};
